import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, BellRing, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  backgroundGeolocation,
  isNativeApp,
  nativeBadge,
  nativeNotifications,
} from '@/features/location/native-bridge';
import { useAppUpdate } from '@/features/app/use-app-update';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CHAT_NOTICE_CHANNEL, ensureNoticeChannel } from './chat-notices';

/** The test notice's own id, so it never overwrites a real one. */
const TEST_NOTICE_ID = 99;

type Permission = 'granted' | 'denied' | 'prompt' | 'unknown';

function Row({
  ok,
  label,
  value,
  children,
}: {
  ok: boolean | null;
  label: string;
  value: string;
  children?: ReactNode;
}) {
  const Icon = ok === null ? Info : ok ? CheckCircle2 : AlertTriangle;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon
        aria-hidden="true"
        className={cn(
          'mt-0.5 size-4 shrink-0',
          ok === null ? 'text-muted-foreground' : ok ? 'text-success' : 'text-warning',
        )}
      />
      <div className="min-w-0 flex-1">
        <span className="text-sm">
          {label}: <span className="font-medium">{value}</span>
        </span>
        {children}
      </div>
    </div>
  );
}

/**
 * Whether the phone can actually raise a chat alert, and what to do when it
 * cannot.
 *
 * This exists because "no notification appeared" has four ordinary causes and
 * the app was silent about all of them: the handset is on a shell older than
 * the one that carries the notification plugins, Android was never granted
 * the notification permission (or it was refused, after which Android stops
 * asking), the launcher is one of the many that draws a dot rather than a
 * number, or the app was closed. Guessing between those over a message costs
 * more than showing them, so the card says which, and the test button proves
 * the whole path in one tap.
 *
 * Only in the shell: in a browser there is no tray, no icon and nothing to
 * fix. Rendered from the dashboard, under the update banner.
 */
export function ChatAlertsCard() {
  const { installed, available } = useAppUpdate();
  const [permission, setPermission] = useState<Permission>('unknown');
  const [badge, setBadge] = useState<boolean | null>(null);
  const [testing, setTesting] = useState(false);

  const plugin = nativeNotifications();
  const native = isNativeApp();

  const readPermission = useCallback(async () => {
    const notifications = nativeNotifications();
    if (!notifications) return;
    try {
      const state = await notifications.checkPermissions();
      setPermission(
        state.display === 'granted' || state.display === 'denied'
          ? state.display
          : 'prompt',
      );
    } catch {
      setPermission('unknown');
    }
  }, []);

  useEffect(() => {
    if (!native) return;
    void readPermission();
    const supported = nativeBadge();
    if (supported) {
      void supported
        .isSupported()
        .then((result) => setBadge(result.isSupported))
        .catch(() => setBadge(null));
    }
  }, [native, readPermission]);

  // Coming back from the phone's settings screen should show the new answer
  // rather than the one read before leaving.
  useEffect(() => {
    if (!native) return;
    const recheck = () => {
      if (document.visibilityState === 'visible') void readPermission();
    };
    document.addEventListener('visibilitychange', recheck);
    return () => document.removeEventListener('visibilitychange', recheck);
  }, [native, readPermission]);

  if (!native) return null;

  const outdated = available !== null;
  const ask = async () => {
    if (!plugin) return;
    try {
      const state = await plugin.requestPermissions();
      // Android answers "denied" only for a real refusal. Swiping the dialog
      // away leaves it at prompt, and calling that denied would drop the
      // button that asks again — sending somebody into the settings screen
      // for a dialog they merely dismissed.
      setPermission(
        state.display === 'granted' || state.display === 'denied' ? state.display : 'prompt',
      );
      if (state.display === 'denied') {
        toast.error('Android blocked it. Open the phone settings below to turn it back on.');
      } else if (state.display !== 'granted') {
        toast.message('No answer from the phone — try again.');
      }
    } catch {
      toast.error('Could not ask this phone for permission.');
    }
  };

  const openSettings = () => {
    // The app's own settings page, where Notifications can be switched back
    // on by hand. Borrowed from the location plugin, which is in every shell.
    void backgroundGeolocation()?.openSettings().catch(() => undefined);
  };

  const test = async () => {
    if (!plugin) return;
    setTesting(true);
    try {
      await ensureNoticeChannel();
      await plugin.schedule({
        notifications: [
          {
            id: TEST_NOTICE_ID,
            channelId: CHAT_NOTICE_CHANNEL,
            title: 'KioBytes Telecom',
            body: 'This is a test. Real messages look like this.',
            extra: { link: '/chat' },
          },
        ],
      });
      // Deliberately no badge here. Setting one would put a "1" on the
      // launcher that nothing takes off: the poll only rewrites the number
      // when the real unread count changes, so with nothing unread the fake
      // one would sit on the icon until the next real message.
      toast.success('Sent. Look at the top of your screen.');
    } catch {
      toast.error('This phone refused the test notification.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BellRing aria-hidden="true" className="size-4" />
          Message alerts
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Row
          ok={!outdated}
          label="App version"
          value={installed?.versionName ?? 'unknown'}
        >
          {outdated && (
            <p className="text-xs text-muted-foreground">
              Version {available.versionName} is out. Update from the banner above — alerts work
              from 1.4, and 1.5 added the buzz and the proper icon.
            </p>
          )}
        </Row>

        {plugin ? (
          <Row
            ok={permission === 'granted'}
            label="Notifications"
            value={
              permission === 'granted'
                ? 'Allowed'
                : permission === 'denied'
                  ? 'Blocked by the phone'
                  : permission === 'prompt'
                    ? 'Not asked yet'
                    : 'Unknown'
            }
          >
            {permission !== 'granted' && (
              <p className="mt-1 flex flex-wrap gap-2">
                {permission !== 'denied' && (
                  <Button size="sm" onClick={() => void ask()}>
                    Turn on notifications
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={openSettings}>
                  Open phone settings
                </Button>
              </p>
            )}
          </Row>
        ) : (
          <Row
            ok={false}
            label="Notifications"
            value="Not in this version of the app"
          >
            <p className="text-xs text-muted-foreground">
              Install the newest app from the banner above, then open this page again.
            </p>
          </Row>
        )}

        <Row
          ok={badge === null ? null : badge}
          label="Number on the app icon"
          value={badge === null ? 'Unknown' : badge ? 'Shown' : 'Not on this phone'}
        >
          {badge === false && (
            <p className="text-xs text-muted-foreground">
              This launcher does not draw numbers. It shows a small dot on the icon instead,
              which comes from the notification itself.
            </p>
          )}
        </Row>

        {plugin && (
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            loading={testing}
            onClick={() => void test()}
          >
            Send a test notification
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
